<template>
  <div class="section">
    <div class="container">
      <div class="columns is-centered">
        <div class="column is-half">
          <div class="section-block">
            <span class="title">Edit Profile Information</span>
          </div>

          <div class="columns is-centered profile-box">
            <div
              v-if="avatar || profile.avatar_thumb_url"
              class="column is-narrow"
            >
              <figure class="image is-180x180">
                <img
                  :src="
                    avatar
                      ? avatar
                      : profile.avatar_thumb_url + '?ts=' + avatar_ts
                  "
                />
              </figure>
            </div>
          </div>

          <div class="is-block upload-box">
            <div class="level">
              <div class="level-left">
                <span v-if="photoFileName && this.form.avatar">{{
                  photoFileName
                }}</span>
                <span v-else>No image selected</span>
              </div>
              <div class="level-right">
                <b-upload accept="image/*" @input="onPhotoSelect">
                  <a class="button is-primary">
                    <span>Upload Image</span>
                  </a>
                </b-upload>
              </div>
            </div>
          </div>

          <validation-observer
            @submit.prevent="submit"
            tag="form"
            ref="edit"
            class="tw-mt-16"
            v-slot="{ pristine }"
          >
            <div class="columns">
              <div class="column">
                <validation-provider
                  vid="first_name"
                  rules="required"
                  name="first name"
                  v-slot="{ errors }"
                >
                  <b-field
                    class="tw-mb-0"
                    :message="errors"
                    :type="{ 'is-danger': errors.length }"
                  >
                    <label for="first_name">
                      First name
                      <b-input
                        class="tw-mt-1"
                        id="first_name"
                        ref="first_name"
                        v-model="form.first_name"
                        @input="checkFieldChanges"
                        autofocus
                      ></b-input>
                    </label>
                  </b-field>
                </validation-provider>
              </div>
              <div class="column">
                <validation-provider
                  vid="last_name"
                  rules="required"
                  name="last name"
                  v-slot="{ errors }"
                >
                  <b-field
                    class="tw-mb-0"
                    :message="errors"
                    :type="{ 'is-danger': errors.length }"
                  >
                    <label for="last_name">
                      Last name
                      <b-input
                        class="tw-mt-1"
                        id="last_name"
                        v-model="form.last_name"
                        @input="checkFieldChanges"
                      ></b-input>
                    </label>
                  </b-field>
                </validation-provider>
              </div>
            </div>
            <validation-provider
              vid="phone_number"
              rules="required"
              name="phone number"
              v-slot="{ errors }"
            >
              <b-field :message="errors" :type="{ 'is-danger': errors.length }">
                <label for="phone_number"
                  >Phone number
                  <b-input
                    class="tw-mt-1"
                    placeholder="phone_number"
                    v-model="form.phone_number"
                    @input="checkFieldChanges"
                  ></b-input>
                </label>
              </b-field>
            </validation-provider>
            <validation-provider vid="address" name="address">
              <b-field>
                <label for="address"
                  >Address <em>(optional)</em>
                  <b-input
                    class="tw-mt-1"
                    id="address"
                    v-model="form.address"
                    @input="checkFieldChanges"
                  ></b-input>
                </label>
              </b-field>
            </validation-provider>
            <validation-provider
              vid="school"
              rules="required"
              name="school"
              v-slot="{ errors }"
            >
              <b-field :message="errors" :type="{ 'is-danger': errors.length }">
                <label for="school"
                  >School
                  <b-input
                    class="tw-mt-1"
                    id="school"
                    v-model="form.school"
                    @input="checkFieldChanges"
                  ></b-input>
                </label>
              </b-field>
            </validation-provider>
            <validation-provider vid="year_level" name="year level">
              <b-field>
                <label for="year_level"
                  >Year level <em>(optional)</em>
                  <b-input
                    class="tw-mt-1"
                    id="year_level"
                    v-model="form.year_level"
                    @input="checkFieldChanges"
                  ></b-input>
                </label>
              </b-field>
            </validation-provider>
            <validation-provider vid="group" name="church or youth group">
              <b-field>
                <label for="group">
                  Church/Youth Group <em>(optional)</em>
                  <b-input
                    class="tw-mt-1"
                    id="group"
                    v-model="form.group"
                    @input="checkFieldChanges"
                  ></b-input>
                </label>
              </b-field>
            </validation-provider>

            <div class="level">
              <div class="level-left"></div>
              <div class="level-right">
                <p class="is-text-good">
                  {{ isUpdated ? 'Updated!' : '' }}
                </p>
                <b-button
                  v-show="!isUpdated"
                  native-type="submit"
                  type="is-primary"
                  :loading="isSubmitting"
                  :disabled="pristine || hasNoFieldChange || isSubmitting"
                  >Update my information</b-button
                >
              </div>
            </div>
          </validation-observer>
        </div>
      </div>
    </div>
    <!-- Cropper modal -->
    <cropper @change="onPhotoCropped" ref="cropper" />
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import Cropper from '@/components/modals/Cropper'
import { each } from 'lodash'
import UploadMixin from '@/utils/mixins/upload'

export default {
  mixins: [UploadMixin],
  metaInfo: {
    title: 'Edit Profile'
  },
  components: {
    Cropper
  },
  data() {
    return {
      isUpdated: false,
      isSubmitting: false,
      hasNoFieldChange: true,
      photoFileName: '',
      profile: [],
      form: {
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        year_level: '',
        group: '',
        avatar: ''
      },
      avatar: null,
      avatar_ts: new Date().getTime()
    }
  },
  mounted() {
    this.$refs.first_name.focus()
    this.fetchProfileDetails()
  },
  watch: {
    profile: function(n, o) {
      if (this.profile) {
        this.form = {
          first_name: n.first_name,
          last_name: n.last_name,
          email: n.email,
          phone_number: n.phone_number,
          address: n.address === ('null' || null) ? '' : n.address,
          year_level: n.year_level === ('null' || null) ? '' : n.year_level,
          group: n.group === ('null' || null) ? '' : n.group,
          school: n.school
        }
      }
    }
  },
  methods: {
    ...mapMutations({
      changeTimestamp: 'auth/changeTimestamp'
    }),
    onPhotoSelect(file) {
      if (this.validateFileSize(file)) {
        this.photoFileName = file.name
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = e => {
          this.$refs.cropper.open(e.target.result)
        }
      }
    },
    onPhotoCropped(file) {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = e => {
        this.avatar = reader.result
        this.form.avatar = file
      }
      this.checkFieldChanges()
    },
    submit() {
      this.isSubmitting = true

      if (!this.checkFieldChanges()) {
        this.sendFormData()
      }
    },
    fetchProfileDetails() {
      this.$api.get('/users/me').then(({ data }) => {
        this.profile = data.data
      })
    },
    checkFieldChanges() {
      each(this.form, (value, key) => {
        switch (key) {
          case 'first_name':
            return (this.hasNoFieldChange = value === this.profile.first_name)
          case 'last_name':
            return (this.hasNoFieldChange = value === this.profile.last_name)
          case 'address':
            return (this.hasNoFieldChange = value === this.profile.address)
          case 'phone_number':
            return (this.hasNoFieldChange = value === this.profile.phone_number)
          case 'school':
            return (this.hasNoFieldChange = value === this.profile.school)
          case 'group':
            return (this.hasNoFieldChange = value === this.profile.group)
          case 'year_level':
            return (this.hasNoFieldChange = value === this.profile.year_level)
          default:
            break
        }
        if (this.photoFileName) {
          this.$refs.edit.flags.pristine = false
          this.hasNoFieldChange = false
        }
        return this.hasNoFieldChange
      })
      return this.hasNoFieldChange
    },
    sendFormData() {
      let formData = new FormData()
      each(this.form, (value, key) => {
        formData.append(key, value)
      })

      this.$api
        .post('/users/me', formData)
        .then(({ response }) => {
          this.fetchProfileDetails()
          this.isUpdated = true
          setTimeout(() => {
            this.isUpdated = false
          }, 5000)
        })
        .catch(({ response }) => {
          if (response.data && response.data.errors) {
            this.$refs.edit.setErrors(response.data.errors)
          }
        })
        .finally(() => {
          this.photoFileName ? this.changeTimestamp() : ''
          this.isSubmitting = false
          this.hasNoFieldChange = true
        })
    }
  }
}
</script>
